import { CustomError, useApi } from 'hooks/api';
import { useSession } from 'contexts/sessionContext';
import { buildUriWithParams } from 'utils/uriHelper';
import { SessionModel } from 'api';
import { useNavigate } from 'react-router-dom';
import { UseQueryOptions, useQuery } from 'react-query';

export interface InviteEmployerEmployeeModel {
    email: string;
    firstName?: string;
    insertion?: string;
    lastName?: string;
    iban?: string;
}

export interface EmployerEmployeePlainModel {
    id: string;
    fullName: string;
    firstName?: string;
    insertion?: string;
    lastName?: string;
    email: string;
    phoneNumber?: string;
    postalCode?: string;
    houseNumber?: string;
    city?: string;
    street?: string;
    iban?: string;
    employerName?: string;
    hasIFitActivities: boolean;
}

export interface EditEmployeeInputModel {
    fullName: string;
    id: string;
    email: string;
    firstName: string;
    insertion?: string;
    lastName: string;
    postalCode?: string;
    city?: string;
    street?: string;
    houseNumber?: string;
    phoneNumber?: string;
}

export interface EmployerEmployeeInvitationModel {
    id: string;
    code: string;
    emailInvitee: string;
    invitedBy: EmployerEmployeePlainModel;
    invitedOn: string;
    isAlreadyKnown: boolean;
    acceptanceName: string;
    tfaEnabled: boolean;
    inviteeId: string;
}

export interface AllEmployerEmployeesQueryOptions {
    completedRegistration?: boolean;
    employerId?: string;
    locationId?: string;
    acceptedEmployerInvite?: string;
}

export interface AllEmployerEmployeeInvitationsQueryOptions {
    completedRegistration?: boolean;
}

export interface CompleteEmployerEmployeeRegistrationModel {
    email: string;
    code: string;
    tfaCode: string;
    firstName: string;
    insertion: string;
    lastName: string;
    password: string;
    confirmPassword: string;
}

export interface AcceptOrDenyEmployerInviteModel {
    accept: boolean;
    code: string;
}

export interface EmployeeVerificationModel {
    email: string;
    firstName?: string;
    insertion?: string;
    lastName?: string;
    iban?: string;
    verified: boolean;
}

export interface ReadImportEmployeeExcelInputModel {
    file?: File;
}

export interface EmployerEmployeeSearchInputModel {
    firstName: string;
    lastName: string;
}

export interface BindSecondEmailInputModel {
    username: string;
    password: string;
    token: string;
}

export interface SimpleEmployerEmployeeModel {
    employerId: string;
    employerName: string;
    employees: SimpleEmployeeModel[];
}

export interface SimpleEmployeeModel {
    employeeId: string;
    employeeName: string;
}

export const useEmployerEmployees = () => {
    const { callApi } = useApi();
    const { session, setSession } = useSession();
    const navigate = useNavigate();

    const allEmployees = async (queryOptions?: AllEmployerEmployeesQueryOptions) => {
        const uri = buildUriWithParams('/EmployerEmployee/v1', queryOptions);

        const response = await callApi.current<EmployerEmployeePlainModel[]>(uri);

        return response;
    };

    const getEmployee = async (id: string) => {
        const response = await callApi.current<EditEmployeeInputModel>(
            `/EmployerEmployee/v1/GetEmployee/${id}`,
            {
                method: 'GET'
            }
        );

        return response;
    };

    const getExcelExample = async () => {
        const response = await callApi.current(
            '/EmployerEmployeeInvitation/v1/ExampleExcel'
        );
        return response;
    };

    const allEmployeeInvitations = async (employerId: string) => {
        const response = await callApi.current<EmployerEmployeeInvitationModel[]>(`/Employer/v1/${employerId}/EmployeeInvitations`);

        return response;
    };

    const verifyEmailKnownExcel = async (excelFileModel: ReadImportEmployeeExcelInputModel) => {
        if (excelFileModel.file !== undefined) {
            const formData = new FormData();
            formData.append('file', excelFileModel.file);
            const response = await callApi.current<EmployeeVerificationModel[]>(
                `/Employer/v1/${session?.activeEmployerId ?? ''}/VerifyEmailExcel`,
                {
                    method: 'POST',
                    body: formData
                });
            return response;
        }
    };

    const inviteExcel = async (inviteEmployeeModels: InviteEmployerEmployeeModel[], employerId: string) => {
        const response = await callApi.current(
            `/Employer/v1/${employerId ?? ''}/InviteMultipleEmployees`,
            {
                method: 'POST',
                body: JSON.stringify(inviteEmployeeModels)
            }
        );
        return response;
    };

    const inviteEmployee = async (inviteEmployeeModel: InviteEmployerEmployeeModel & { employerId: string; }) => {
        const response = await callApi.current(
            `/Employer/v1/${inviteEmployeeModel.employerId}/InviteEmployee`,
            {
                method: 'POST',
                body: JSON.stringify(inviteEmployeeModel)
            }
        );

        return response;
    };

    const cancelEmployeeInvitation = async (invitationId: string) => {
        const response = await callApi.current(
            `/EmployerEmployeeInvitation/v1/${invitationId}`,
            {
                method: 'DELETE'
            }
        );

        return response;
    };

    const resendEmployeeInvitation = async (invitationId: string) => {
        const response = await callApi.current(
            `/EmployerEmployeeInvitation/v1/${invitationId}/resend`,
            {
                method: 'POST'
            }
        );

        return response;
    };

    const sendRegistrationTfaCode = async (invitationCode: string) => {
        const response = await callApi.current(
            '/EmployerEmployee/v1/sendRegistrationTfaCode',
            {
                method: 'POST',
                body: JSON.stringify({ code: invitationCode })
            }
        );

        return response;
    };

    const completeRegistration = async (completeAdminRegistrationModel: CompleteEmployerEmployeeRegistrationModel) => {
        const response = await callApi.current<SessionModel>(
            '/EmployerEmployee/v1/CompleteRegistration',
            {
                method: 'POST',
                body: JSON.stringify(completeAdminRegistrationModel)
            }
        );

        if (response.ok && response.data) {
            const data = response.data;

            if (data.requiresVerification) {
                navigate('/account/tfa');
            } else {
                setSession(data.claims);
                navigate('/');
            }
        }

        return response;
    };

    const validateTfaCode = async (tfaCode: string, registrationCode: string) => {
        const response = await callApi.current(
            '/EmployerEmployee/v1/ValidateTfa',
            {
                method: 'POST',
                body: JSON.stringify({
                    tfaCode,
                    code: registrationCode
                })
            }
        );

        return response;
    };

    const invitationInfo = async (registrationCode: string) => {
        const response = await callApi.current<EmployerEmployeeInvitationModel>(
            `/EmployerEmployeeInvitation/v1/${registrationCode}`
        );

        return response;
    };

    const acceptOrDenyEmployerInvite = async (values: AcceptOrDenyEmployerInviteModel) => {
        const response = await callApi.current(
            `/EmployerEmployeeInvitation/v1/${values.code}/${values.accept ? 'Accept' : 'Deny'}`,
            {
                method: 'POST'
            }
        );

        return response;
    };

    const removeEmployeeFromEmployer = async (userId: string) => {
        const response = await callApi.current(
            `/Employer/v1/${session?.activeEmployerId ?? ''}/Employee`,
            {
                method: 'DELETE',
                body: JSON.stringify({ userId })
            }
        );

        return response;
    };

    const editEmployee = async (values: EditEmployeeInputModel) => {
        const response = await callApi.current('/EmployerEmployee/v1/Edit', {
            method: 'PUT',
            body: JSON.stringify(values)
        });
        return response;
    };

    const searchEmployees = async (model: EmployerEmployeeSearchInputModel) => {
        const response = await callApi.current<EmployerEmployeePlainModel[]>('/EmployerEmployee/v1/search', {
            method: 'POST',
            body: JSON.stringify(model)
        });

        return response;
    };

    const useSearchEmployees = (model: EmployerEmployeeSearchInputModel, options?: UseQueryOptions<EmployerEmployeePlainModel[], CustomError[]>) => {
        const queryKey = ['employerEmployees.search', model];

        return useQuery<EmployerEmployeePlainModel[], CustomError[]>(queryKey, async () => {
            const response = await searchEmployees(model);

            if (!response.ok && response.errors != null) {
                throw response.errors;
            }

            return response.data ?? [];
        }, options);
    };

    const bindSecondEmail = async (model: BindSecondEmailInputModel) => {
        const response = await callApi.current('/EmployerEmployee/v1/setSecondaryEmail', { method: 'PUT', body: JSON.stringify(model) });
        return response;
    };

    const getEmployerOfInvitee = async (inviteeId: string) => {
        const response = await callApi.current<string>(`/EmployerEmployee/v1/inviteeEmployer/${inviteeId}`, { method: 'Get' });
        return response;
    };

    const getSimpleEmployerEmployees = async () => {
        const response = await callApi.current<SimpleEmployerEmployeeModel[]>('/EmployerEmployee/v1/simpleAllEmployerEmployees');
        return response;
    };

    return {
        allEmployees,
        editEmployee,
        getEmployee,
        allEmployeeInvitations,
        inviteEmployee,
        cancelEmployeeInvitation,
        resendEmployeeInvitation,
        sendRegistrationTfaCode,
        completeRegistration,
        validateTfaCode,
        invitationInfo,
        acceptOrDenyEmployerInvite,
        removeEmployeeFromEmployer,
        verifyEmailKnownExcel,
        inviteExcel,
        getExcelExample,
        useSearchEmployees,
        bindSecondEmail,
        getEmployerOfInvitee,
        getSimpleEmployerEmployees
    };
};
